// client/src/components/TeamDetailLayout.js
//
// Parent route for /teams/:teamId/*. Mounts the mobile shell once
// (PfShell + PfTopBar + PfTabBar) and renders whichever tab component
// is active via <Outlet />. Switching tabs doesn't remount the shell
// or refetch the team — only the tab content changes.
//
// Fetches the single team via /teams/:id so the top bar can show its
// name and tier without each tab having to re-fetch. If a tab needs
// its own data (roster, schedule, blocks), it fetches on its own —
// this layout only owns the shell.
//
// Also owns drawer state for the team-scoped chrome. The drawer is
// the same PfAppDrawer used by AppChromeLayout, but here we pass
// teamContext so it shows the "Switch team" escape hatch — otherwise
// mobile users get stuck inside a team with no obvious way back to
// the app-level views.

import { useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import { useOrg } from '../lib/OrgProvider';
import { PfShell } from './shell/PfShell';
import { PfTopBar } from './shell/PfTopBar';
import { PfTabBar } from './shell/PfTabBar';
import { PfAppDrawer } from './shell/PfAppDrawer';

export function TeamDetailLayout() {
  const { teamId } = useParams();
  const { activeOrg, signOut } = useOrg();
  const [menuOpen, setMenuOpen] = useState(false);

  // Fetch the single team. Endpoint returns { success, data: { id,
  // name, tier, ... } } — see GET /teams/:id in functions/ysa-api/index.js.
  // 404 (team not in this org) surfaces as `error`; we render a friendly
  // fallback rather than crashing the shell.
  const { data: team, loading, error } = useFetch(`/teams/${teamId}`);

  // TODO: swap this to a team-scoped role endpoint when it lands.
  // For now, use the highest-privilege org-scoped role — same logic
  // as AnnouncementsHome. This means a Guardian who's also a Coach in
  // the org sees COACH on every team's top bar, even teams where they
  // aren't actually coaching. Fine for now; refine when the team-scoped
  // endpoint is available.
  const topRole = (() => {
    const roles = activeOrg?.roles ?? [];
    if (roles.includes('Admin')) return 'ADMIN';
    if (roles.includes('Coach')) return 'COACH';
    if (roles.includes('Guardian')) return 'PARENT';
    return 'PARENT';
  })();

  const topBar = (
    <PfTopBar
      teamName={team?.name ?? (loading ? '…' : 'Team')}
      tier={team?.tier ?? null}
      role={topRole}
      logoUrl={null}
      onMenuClick={() => setMenuOpen(true)}
    />
  );

  const tabBar = <PfTabBar teamId={teamId} />;

  // Team context for the drawer. Only pass real team data once loaded
  // — passing a half-loaded {id, name: undefined} shape would render
  // an empty "Current team" section during the fetch flash.
  const teamContext = team ? { id: team.id, name: team.name } : null;

  return (
    <>
      <PfShell topBar={topBar} tabBar={tabBar}>
        {error ? (
          // Team not found or not in this org. Keep the shell up so the
          // user can tab-navigate away rather than crash the whole app.
          <div className="px-5 pt-6">
            <h2 className="font-serif text-[22px] text-ink">Team not found.</h2>
            <p className="text-inkSub text-sm mt-2">
              This team doesn't exist or isn't part of your organization.
            </p>
          </div>
        ) : (
          <Outlet context={{ team, teamLoading: loading }} />
        )}
      </PfShell>

      {/* Drawer sits outside PfShell so its overlay + panel can span
          the full viewport, not just the mobile-frame column. */}
      <PfAppDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSignOut={signOut}
        teamContext={teamContext}
      />
    </>
  );
}